import * as React from 'react';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import './StatusModal.scss';

interface Props {
  isOpen: boolean;
  status?: 'success' | 'info' | 'warning' | 'error';
  title: string;
  message?: string;
  lines?: string[];
  onCloseIconClick?: () => void;
  onClose: () => void;
  primaryButtonText?: string;
  onPrimaryClick?: () => void;
  cancelButtonText?: string;
  children?: React.ReactNode;
  compact?: boolean;
}

const STATUS_META = {
  success: {
    icon: '✓',
    iconBg: '#28a745',
    panelBg: 'linear-gradient(135deg, #f3fff7 0%, #eefaf3 100%)',
    borderColor: 'rgba(40, 167, 69, 0.18)'
  },
  info: {
    icon: 'i',
    iconBg: '#17a2b8',
    panelBg: 'linear-gradient(135deg, #f3fcff 0%, #eef8fb 100%)',
    borderColor: 'rgba(23, 162, 184, 0.18)'
  },
  warning: {
    icon: '!',
    iconBg: '#f0ad4e',
    panelBg: 'linear-gradient(135deg, #fffaf2 0%, #fff5e8 100%)',
    borderColor: 'rgba(240, 173, 78, 0.18)'
  },
  error: {
    icon: '×',
    iconBg: '#dc3545',
    panelBg: 'linear-gradient(135deg, #fff5f6 0%, #fff0f2 100%)',
    borderColor: 'rgba(220, 53, 69, 0.18)'
  }
};

export const StatusModal: React.FC<Props> = ({
  isOpen,
  status = 'success',
  title,
  message,
  lines = [],
  onCloseIconClick,
  onClose,
  primaryButtonText,
  onPrimaryClick,
  cancelButtonText = 'Ok',
  children,
  compact = false
}) => {
  if (!isOpen) return null;

  const meta = STATUS_META[status] || STATUS_META.success;
  const safeLines = Array.isArray(lines) ? lines.filter(Boolean) : [];
  const hasCustomContent = React.Children.count(children) > 0;

  const compactContent = (
    <div className={`statusModalCompact statusModalCompact--${status}`}>
      <div className="statusModalCompact__summary">
        <span className="statusModalCompact__icon" aria-hidden="true">
          {meta.icon}
        </span>
        <div className="statusModalCompact__copy">
          <h2>{title}</h2>
          {message ? <p>{message}</p> : null}
        </div>
      </div>

      {hasCustomContent ? (
        <div className="statusModalCompact__details">{children}</div>
      ) : safeLines.length > 0 ? (
        <div className="statusModalCompact__details">
          {safeLines.map((line, index) => (
            <p key={index}>{line}</p>
          ))}
        </div>
      ) : null}

      <div className="statusModalCompact__actions">
        {primaryButtonText && onPrimaryClick ? (
          <>
            <button type="button" className="statusModalCompact__secondary" onClick={onClose}>
              {cancelButtonText}
            </button>
            <button type="button" className="statusModalCompact__primary" onClick={onPrimaryClick}>
              {primaryButtonText}
            </button>
          </>
        ) : (
          <button type="button" className="statusModalCompact__primary" onClick={onClose}>
            {cancelButtonText}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <SimpleModal
      isOpen={isOpen}
      title={title}
      onCloseClick={onClose}
      onCloseIconClick={onCloseIconClick}
      buttonCancelText={cancelButtonText}
      buttonText={primaryButtonText}
      onButtonClick={onPrimaryClick}
      isBodyBold={false}
      customClass={compact ? 'statusModal--compact' : 'modal-lg'}
      dialogStyle={compact ? { maxWidth: 460 } : undefined}
      showHeader={!compact}
      showFooter={!compact}
    >
      {compact ? (
        compactContent
      ) : (
      <div className="p-1">
        <div
          className="border rounded p-3 mb-3"
          style={{
            background: meta.panelBg,
            borderColor: meta.borderColor
          }}
        >
          <div className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: meta.iconBg,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 20,
                lineHeight: 1,
                flex: '0 0 auto'
              }}
            >
              {meta.icon}
            </div>

            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: '#1f2d3d',
                lineHeight: 1.25
              }}
            >
              {title}
            </div>
          </div>

          {message ? <div className="text-muted">{message}</div> : null}
        </div>

        {hasCustomContent ? (
          <div className="border rounded p-3" style={{ background: '#fff' }}>
            {children}
          </div>
        ) : safeLines.length > 0 ? (
          <div className="border rounded p-3" style={{ background: '#fff' }}>
            <ul className="mb-0 pl-3 text-muted">
              {safeLines.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      )}
    </SimpleModal>
  );
};

StatusModal.displayName = 'StatusModal';
